import { useEffect, useRef } from "react";
import { projector, riskOf, shiftBelt } from "@/lib/geo";
import { modisUrl as _m } from "@/lib/api";

export default function FlowMap({ data, layers, forecastHour, selectedIsland, onSelectIsland }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const stateRef = useRef({ islands: [], proj: null, W: 0, H: 0 });

  // load MODIS backdrop once
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = _m({ width: 1024, height: 560 });
    img.onload = () => (imgRef.current = img);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !data) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const bbox = data.bbox;

    let W = 0, H = 0, proj;
    const resize = () => {
      const r = wrap.getBoundingClientRect();
      W = r.width;
      H = r.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      proj = projector(bbox, W, H);
      stateRef.current.proj = proj;
      stateRef.current.W = W;
      stateRef.current.H = H;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const flows = (data.flowlines || []).map((f) => ({
      lng: f.start.lng,
      lat: f.start.lat,
      dx: f.end.lng - f.start.lng,
      dy: f.end.lat - f.start.lat,
      spd: f.speed,
    }));

    const nearestFlow = (lng, lat) => {
      let best = flows[0];
      let bd = 1e9;
      for (const f of flows) {
        const d = (f.lng - lng) ** 2 + (f.lat - lat) ** 2;
        if (d < bd) {
          bd = d;
          best = f;
        }
      }
      return best;
    };

    const [w, s, e, n] = bbox;
    const rnd = () => ({
      lng: w + Math.random() * (e - w),
      lat: s + Math.random() * (n - s),
      age: Math.random() * 100,
      max: 60 + Math.random() * 90,
    });
    const N = 300;
    let parts = Array.from({ length: N }, rnd);

    const belt = shiftBelt(data.belt || [], forecastHour);
    stateRef.current.islands = data.islands || [];

    // biomass "mat" particles that stream from dense belt toward the coasts
    const dense = belt.filter((p) => p.density > 0.22);
    const spawnMat = () => {
      const src = dense.length
        ? dense[Math.floor(Math.random() * dense.length)]
        : { lng: (w + e) / 2, lat: (s + n) / 2 };
      return {
        lng: src.lng + (Math.random() - 0.5) * 1.3,
        lat: src.lat + (Math.random() - 0.5) * 1.3,
        age: Math.random() * 60,
        max: 130 + Math.random() * 120,
      };
    };
    let mats = Array.from({ length: 190 }, spawnMat);

    let t = 0;
    const draw = () => {
      t += 1;
      // backdrop
      if (layers.satellite && imgRef.current) {
        ctx.globalAlpha = 1;
        ctx.drawImage(imgRef.current, 0, 0, W, H);
        ctx.fillStyle = "rgba(3,11,30,0.5)";
        ctx.fillRect(0, 0, W, H);
      } else {
        const g = ctx.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, "#04122c");
        g.addColorStop(1, "#020a1c");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      }

      // biomass heat
      if (layers.biomass) {
        for (const p of belt) {
          if (p.density < 0.15) continue;
          const x = proj.x(p.lng);
          const y = proj.y(p.lat);
          if (x < -40 || x > W + 40 || y < -40 || y > H + 40) continue;
          const rad = 10 + p.density * 26;
          const grd = ctx.createRadialGradient(x, y, 0, x, y, rad);
          grd.addColorStop(0, `rgba(0,255,135,${0.14 + p.density * 0.4})`);
          grd.addColorStop(1, "rgba(0,255,135,0)");
          ctx.fillStyle = grd;
          ctx.beginPath();
          ctx.arc(x, y, rad, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // streaming biomass mats (shows the direction the seaweed is moving)
      if (layers.biomass) {
        ctx.globalCompositeOperation = "lighter";
        for (const m of mats) {
          const f = nearestFlow(m.lng, m.lat);
          const px = proj.x(m.lng);
          const py = proj.y(m.lat);
          m.lng += f.dx * 0.09 - 0.006; // flow + steady westward bias
          m.lat += f.dy * 0.09;
          m.age += 1;
          const nx = proj.x(m.lng);
          const ny = proj.y(m.lat);
          const life = Math.max(0, 1 - m.age / m.max);
          ctx.strokeStyle = `rgba(0,255,135,${life * 0.35})`;
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(nx, ny);
          ctx.stroke();
          ctx.fillStyle = `rgba(140,255,190,${life * 0.9})`;
          ctx.beginPath();
          ctx.arc(nx, ny, 2.3, 0, Math.PI * 2);
          ctx.fill();
          if (m.age > m.max || m.lng < w || m.lat < s || m.lat > n) Object.assign(m, spawnMat());
        }
        ctx.globalCompositeOperation = "source-over";
      }

      // flow particles
      if (layers.flow) {
        ctx.lineWidth = 1.1;
        for (const p of parts) {
          const f = nearestFlow(p.lng, p.lat);
          const px = proj.x(p.lng);
          const py = proj.y(p.lat);
          const step = 0.05;
          p.lng += f.dx * step;
          p.lat += f.dy * step;
          p.age += 1;
          const nx = proj.x(p.lng);
          const ny = proj.y(p.lat);
          const a = Math.min(1, (p.max - p.age) / p.max) * 0.7;
          ctx.strokeStyle = `rgba(0,229,255,${a})`;
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(nx, ny);
          ctx.stroke();
          ctx.fillStyle = `rgba(180,245,255,${a})`;
          ctx.beginPath();
          ctx.arc(nx, ny, 1.2, 0, Math.PI * 2);
          ctx.fill();
          if (p.age > p.max || p.lng > e || p.lng < w || p.lat > n || p.lat < s) {
            Object.assign(p, rnd());
          }
        }
      }

      // islands
      if (layers.islands) {
        for (const isl of data.islands || []) {
          const x = proj.x(isl.lng);
          const y = proj.y(isl.lat);
          if (x < 0 || x > W || y < 0 || y > H) continue;
          const rc = riskOf(isl.risk);
          const sel = isl.id === selectedIsland;
          if (isl.risk === "HIGH" || isl.risk === "MODERATE") {
            const pr = (t % 90) / 90;
            ctx.strokeStyle = `rgba(255,159,28,${0.6 * (1 - pr)})`;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(x, y, 6 + pr * 22, 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.fillStyle = rc.hex;
          ctx.beginPath();
          ctx.arc(x, y, sel ? 6 : 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = sel ? "#00e5ff" : "rgba(3,11,30,0.9)";
          ctx.lineWidth = sel ? 2 : 1;
          ctx.stroke();
          ctx.font = "600 11px 'JetBrains Mono', monospace";
          ctx.fillStyle = sel ? "#00e5ff" : "rgba(226,232,240,0.9)";
          ctx.fillText(isl.name, x + 9, y + 3);
        }
      }

      // drift-direction compass (mean flow vector) — shows which way mats move
      {
        let mdx = 0, mdy = 0;
        for (const f of flows) {
          mdx += f.dx;
          mdy += f.dy;
        }
        mdx /= flows.length || 1;
        mdy /= flows.length || 1;
        const ang = Math.atan2(-mdy, mdx);
        const cx = W / 2;
        const cy = 34;
        const len = 40;
        const ex = cx + Math.cos(ang) * len;
        const ey = cy + Math.sin(ang) * len;
        ctx.strokeStyle = "rgba(255,159,28,0.95)";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(cx - Math.cos(ang) * len, cy - Math.sin(ang) * len);
        ctx.lineTo(ex, ey);
        ctx.stroke();
        // arrowhead
        const a1 = ang + Math.PI - 0.4;
        const a2 = ang + Math.PI + 0.4;
        ctx.fillStyle = "rgba(255,159,28,0.95)";
        ctx.beginPath();
        ctx.moveTo(ex, ey);
        ctx.lineTo(ex + Math.cos(a1) * 11, ey + Math.sin(a1) * 11);
        ctx.lineTo(ex + Math.cos(a2) * 11, ey + Math.sin(a2) * 11);
        ctx.closePath();
        ctx.fill();
        ctx.font = "700 10px 'JetBrains Mono', monospace";
        ctx.fillStyle = "rgba(255,209,102,0.9)";
        ctx.textAlign = "center";
        ctx.fillText("SARGASSUM DRIFT", cx, cy + 26);
        ctx.textAlign = "left";
      }

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [data, layers, forecastHour, selectedIsland]);

  const handleClick = (ev) => {
    const { proj, islands } = stateRef.current;
    if (!proj) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = ev.clientX - rect.left;
    const my = ev.clientY - rect.top;
    let hit = null;
    let bd = 400;
    for (const isl of islands) {
      const d = (proj.x(isl.lng) - mx) ** 2 + (proj.y(isl.lat) - my) ** 2;
      if (d < bd) {
        bd = d;
        hit = isl;
      }
    }
    if (hit) onSelectIsland?.(hit.id);
  };

  return (
    <div ref={wrapRef} className="relative h-full w-full" data-testid="flow-map">
      <canvas ref={canvasRef} onClick={handleClick} className="h-full w-full cursor-crosshair" />
    </div>
  );
}
